import React, { useState, useRef, useEffect } from 'react'
import { Pencil, Check, X, Loader } from 'lucide-react'
import './InlineEditField.css'

/**
 * InlineEditField - Edit-in-place component for simple fields like phone/email
 * 
 * Props:
 * - label: Field label displayed above the value
 * - value: Current value (can be null/undefined)
 * - fieldName: API field name for the update request
 * - onSave: Async function(fieldName, newValue) => Promise - called to save changes
 * - placeholder: Placeholder text when value is empty
 * - type: Input type ('text', 'email', 'tel') - default 'text'
 * - validator: Optional function(value) => { valid: boolean, error?: string }
 * - formatDisplay: Optional function to format the displayed value
 * - icon: Optional icon component to display before the value
 */
function InlineEditField({
  label,
  value,
  fieldName,
  onSave,
  placeholder = 'Not provided',
  type = 'text',
  validator = null,
  formatDisplay = null,
  icon: Icon = null
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value || '')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const inputRef = useRef(null)

  // Reset edit value when original value changes
  useEffect(() => {
    setEditValue(value || '')
  }, [value])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleEdit = () => {
    setError(null)
    setSuccess(false)
    setEditValue(value || '')
    setIsEditing(true)
  }

  const handleCancel = () => {
    setEditValue(value || '')
    setError(null)
    setIsEditing(false)
  }

  const handleSave = async () => {
    // Validate if validator provided
    if (validator) {
      const result = validator(editValue)
      if (!result.valid) {
        setError(result.error || 'Invalid value')
        return
      }
    }

    setIsSaving(true)
    setError(null)

    try {
      await onSave(fieldName, editValue || null)
      setIsEditing(false)
      setSuccess(true)
      // Clear success message after 2 seconds
      setTimeout(() => setSuccess(false), 2000)
    } catch (err) {
      // Provide user-friendly error messages
      let errorMessage = 'Failed to save. Please try again later.'
      
      if (err.message) {
        // Check for specific error types and provide friendly messages
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          errorMessage = 'Unable to connect to server. Please check your connection and try again.'
        } else if (err.message.includes('500') || err.message.includes('Internal Server Error')) {
          errorMessage = 'A server error occurred. Please try again later.'
        } else if (err.message.includes('400') || err.message.includes('Bad Request')) {
          errorMessage = 'Invalid data provided. Please check and try again.'
        } else if (err.message.includes('403') || err.message.includes('Forbidden')) {
          errorMessage = 'You do not have permission to make this change.'
        } else if (err.message.includes('Invalid phone')) {
          errorMessage = err.message // Keep validation messages as-is
        } else if (err.message.includes('Invalid email')) {
          errorMessage = err.message // Keep validation messages as-is
        }
      }
      
      setError(errorMessage)
      console.error('Error updating patient field:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const displayValue = value
    ? (formatDisplay ? formatDisplay(value) : value)
    : placeholder

  return (
    <div className={`inline-edit-field ${isEditing ? 'editing' : ''} ${success ? 'success' : ''}`}>
      <label className="inline-edit-label">{label}</label>
      
      <div className="inline-edit-content">
        {isEditing ? (
          <div className="inline-edit-input-group">
            <input
              ref={inputRef}
              type={type}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`inline-edit-input ${error ? 'has-error' : ''}`}
              placeholder={placeholder}
              disabled={isSaving}
            />
            <div className="inline-edit-actions">
              <button
                className="inline-edit-btn save"
                onClick={handleSave}
                disabled={isSaving}
                title="Save (Enter)"
              >
                {isSaving ? <Loader size={14} className="spinner" /> : <Check size={14} />}
              </button>
              <button
                className="inline-edit-btn cancel"
                onClick={handleCancel}
                disabled={isSaving}
                title="Cancel (Escape)"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ) : (
          <div className="inline-edit-display">
            {Icon && <Icon size={14} className="inline-edit-icon" />}
            <span className={`inline-edit-value ${!value ? 'placeholder' : ''}`}>
              {displayValue}
            </span>
            <button
              className="inline-edit-btn edit"
              onClick={handleEdit}
              title={`Edit ${label}`}
            >
              <Pencil size={14} />
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="inline-edit-error">{error}</div>
      )}
      
      {success && (
        <div className="inline-edit-success">Saved!</div>
      )}
    </div>
  )
}

// Phone number validator - US format (10 digits)
export const phoneValidator = (value) => {
  if (!value || value.trim() === '') {
    return { valid: true } // Empty is allowed
  }
  
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '')
  
  if (digits.length === 10) {
    return { valid: true }
  } else if (digits.length === 11 && digits[0] === '1') {
    return { valid: true }
  }
  
  return {
    valid: false,
    error: 'Phone must be 10 digits (e.g., 555-123-4567)'
  }
}

// Email validator
export const emailValidator = (value) => {
  if (!value || value.trim() === '') {
    return { valid: true } // Empty is allowed
  }
  
  // Basic email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  
  if (emailRegex.test(value)) {
    return { valid: true }
  }
  
  return {
    valid: false,
    error: 'Please enter a valid email address'
  }
}

// Phone number formatter for display
export const formatPhoneDisplay = (phone) => {
  if (!phone) return phone
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')
  
  // Format as (XXX) XXX-XXXX
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  } else if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  
  return phone // Return original if can't format
}

export default InlineEditField